'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, User, Phone, Mail, Calendar, Building } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { leads } from '../data/mock-data';
import { formatCurrency } from '../../../types';
import { leadStatusConfig, leadSourceConfig, type Lead, type LeadStatus } from '../types';

// 리드 등록 모달
function LeadAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="리드 등록"
      description="새로운 잠재 고객(리드) 정보를 등록합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="이름" required>
            <FormInput placeholder="이름을 입력하세요" />
          </FormField>
          <FormField label="회사명">
            <FormInput placeholder="회사명을 입력하세요" />
          </FormField>
          <FormField label="이메일">
            <FormInput type="email" placeholder="example@email.com" />
          </FormField>
          <FormField label="전화번호">
            <FormInput placeholder="010-0000-0000" />
          </FormField>
          <FormField label="리드 소스" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {Object.entries(leadSourceConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="예상 매출">
            <FormInput type="number" placeholder="0" />
          </FormField>
          <FormField label="담당자">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="u001">김영업</option>
              <option value="u002">이마케팅</option>
              <option value="u003">박관리</option>
            </FormSelect>
          </FormField>
          <FormField label="다음 연락일">
            <FormInput type="date" />
          </FormField>
        </div>
        <FormField label="설명">
          <FormTextarea rows={3} placeholder="리드에 대한 추가 정보를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function LeadListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return leads.filter((lead) => {
      const matchesSearch =
        lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (lead.company && lead.company.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (lead.email && lead.email.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesStatus = selectedStatus === 'all' || lead.status === selectedStatus;
      const matchesSource = selectedSource === 'all' || lead.source === selectedSource;

      return matchesSearch && matchesStatus && matchesSource;
    });
  }, [searchQuery, selectedStatus, selectedSource]);

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    leads.forEach((lead) => {
      stats[lead.status] = (stats[lead.status] || 0) + 1;
    });
    return stats;
  }, []);

  const getStatusColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    return colors[color] || colors.blue;
  };

  const columns = [
    {
      key: 'name',
      label: '리드',
      width: '20%',
      sortable: true,
      render: (value: unknown, row: Lead) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{String(value)}</div>
            {row.company && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Building className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{row.company}</span>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      label: '연락처',
      width: '20%',
      render: (value: unknown, row: Lead) => (
        <div className="text-sm min-w-0">
          {value ? (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Mail className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{String(value)}</span>
            </div>
          ) : null}
          {row.phone && (
            <div className="flex items-center gap-1 text-muted-foreground mt-0.5">
              <Phone className="w-3 h-3 flex-shrink-0" />
              <span className="whitespace-nowrap">{row.phone}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'source',
      label: '소스',
      width: '10%',
      render: (value: unknown) => {
        const sourceConfig = leadSourceConfig[value as keyof typeof leadSourceConfig];
        return <span className="text-sm whitespace-nowrap">{sourceConfig?.label || String(value)}</span>;
      },
    },
    {
      key: 'expectedRevenue',
      label: '예상 매출',
      width: '14%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="whitespace-nowrap">{value ? formatCurrency(value as number) : '-'}</span>
      ),
    },
    {
      key: 'assignedToName',
      label: '담당자',
      width: '10%',
      render: (value: unknown) => (
        <span className="text-sm whitespace-nowrap">{String(value || '-')}</span>
      ),
    },
    {
      key: 'nextContactDate',
      label: '다음 연락',
      width: '10%',
      render: (value: unknown) =>
        value ? (
          <div className="flex items-center gap-1 text-sm whitespace-nowrap">
            <Calendar className="w-3 h-3 text-muted-foreground flex-shrink-0" />
            {String(value)}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'status',
      label: '상태',
      width: '8%',
      render: (value: unknown) => {
        const status = value as LeadStatus;
        const config = leadStatusConfig[status];
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(config.color)}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '8%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="리드 관리"
        description="잠재 고객(리드)을 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            리드 등록
          </button>
        }
      />

      {/* 상태별 요약 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(leadStatusConfig).map(([status, config]) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
            className={`p-3 rounded-lg border text-left transition-colors ${
              selectedStatus === status
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:bg-accent/50'
            }`}
          >
            <div className="text-xs text-muted-foreground">{config.label}</div>
            <div className="text-xl font-bold mt-1">{statusStats[status] || 0}건</div>
          </button>
        ))}
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="이름, 회사, 이메일 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(leadStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label}
            </option>
          ))}
        </select>

        <select
          value={selectedSource}
          onChange={(e) => setSelectedSource(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 소스</option>
          {Object.entries(leadSourceConfig).map(([source, config]) => (
            <option key={source} value={source}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 리드 등록 모달 */}
      <LeadAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
